import React, { useState } from "react";
import FieldSection, { Field } from "../general/FieldSection";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { CreateWorkshopInstanceDto, WorkshopDto, WorkshopInstanceDto } from "../../types/workshopTypes";
import { AxiosResponse } from "axios";
import { MembersFieldDto } from "../../types/membersTypes";

interface AddWorkshopInstanceDialogProps {
  workshop: WorkshopDto;
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    workshopId: number,
    workshopInstanceData: CreateWorkshopInstanceDto
  ) => Promise<AxiosResponse<WorkshopInstanceDto>>;
  members: MembersFieldDto[];
}

/**
 * Displays a dialog to add a new workshop instance.
 * @param workshop The workshop to add the instance to.
 * @param isOpen Whether the dialog should be open.
 * @param onClose Function to close the dialog.
 * @param onSave Function to save the workshop instance.
 * @param members The members to add or edit.
 * @returns A dialog to add a new workshop instance.
 */
const AddWorkshopInstanceDialog = ({ workshop, isOpen, onClose, onSave, members }: AddWorkshopInstanceDialogProps) => {
  const [formData, setFormData] = useState<CreateWorkshopInstanceDto>({
    date: undefined,
    startTime: undefined,
    endTime: undefined,
    location: undefined,
    targetGroup: undefined,
    maximumParticipants: undefined,
    referents: [],
  });

  const [errorDate, setErrorDate] = useState(false);
  const [errorStartTime, setErrorStartTime] = useState(false);
  const [errorEndTime, setErrorEndTime] = useState(false);

  const handleDateChange = (value: unknown) => {
    const date = value as Date;
    setFormData({ ...formData, date: date });
  };

  const onChangeStartTime = (value: unknown) => {
    setErrorStartTime(false);
    setErrorEndTime(false);
    if (formData.endTime ? new Date(formData.endTime).getTime() < new Date(value as Date).getTime() : null) {
      setErrorStartTime(true);
    }
    if (formData.endTime ? new Date(formData.endTime).getTime() > new Date(value as Date).getTime() : null) {
      setErrorEndTime(true);
    }
    setFormData({ ...formData, startTime: value as Date });
  };

  const onChangeEndTime = (value: unknown) => {
    setErrorEndTime(false);
    setErrorStartTime(false);
    if (!formData.startTime) {
      setErrorEndTime(true);
    }
    if (formData.startTime ? new Date(formData.startTime).getTime() > new Date(value as Date).getTime() : null) {
      setErrorEndTime(true);
    }
    setFormData({ ...formData, endTime: value as Date });
  };

  const onChangeLocation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, location: event.target.value });
  };

  const onChangeTargetGroup = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, targetGroup: event.target.value });
  };

  const onChangeInternalInstructors = (_event: React.ChangeEvent<object>, value: MembersFieldDto[]) => {
    setFormData({ ...formData, referents: value });
  };

  const onChangeMaxParticipants = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, maximumParticipants: Number(event.target.value) });
  };

  const handleClose = () => {
    setErrorDate(false);
    setErrorStartTime(false);
    setErrorEndTime(false);
    setFormData({
      date: undefined,
      startTime: undefined,
      endTime: undefined,
      location: undefined,
      targetGroup: undefined,
      maximumParticipants: undefined,
      referents: [],
    });
    onClose();
  };

  const handleSave = () => {
    if (errorDate || errorStartTime || errorEndTime) {
      return;
    }
    if (!formData.date) {
      setErrorDate(true);
      return;
    }
    if (formData.startTime && !formData.endTime) {
      setErrorEndTime(true);
      return;
    }
    if (formData.endTime && !formData.startTime) {
      setErrorStartTime(true);
      return;
    }
    onSave(workshop.workshopId, formData);
    handleClose();
  };

  const dialogFields: Array<Field> = [
    {
      label: "Datum",
      type: "Date",
      state: formData.date,
      width: "half",
      onChangeCallback: handleDateChange,
      error: errorDate,
      helperText: errorDate ? "Bitte lege ein gültiges Datum fest" : "",
    },
    {
      label: "Startzeit",
      type: "Time",
      state: formData.startTime,
      width: "half",
      onChangeCallback: onChangeStartTime,
      helperText: errorStartTime ? "Bitte gib eine gültige Startzeit an" : "",
      error: errorStartTime,
    },
    {
      label: "Endzeit",
      type: "Time",
      state: formData.endTime,
      width: "half",
      onChangeCallback: onChangeEndTime,
      helperText: errorEndTime ? "Bitte gib eine gültige Endzeit an" : "",
      error: errorEndTime,
    },
    {
      label: "Ort",
      type: "Text",
      state: formData.location,
      width: "half",
      onChangeCallback: onChangeLocation,
    },
    {
      label: "Zielgruppe",
      type: "Text",
      state: formData.targetGroup,
      width: "half",
      onChangeCallback: onChangeTargetGroup,
    },
    {
      label: "Max. Teilnehmer",
      type: "Text",
      inputType: "number",
      state: formData.maximumParticipants,
      width: "half",
      onChangeCallback: onChangeMaxParticipants,
    },
    {
      label: "Referenten",
      type: "MembersAutocomplete",
      state: formData.referents,
      width: "full",
      onChangeCallback: onChangeInternalInstructors,
      options: members,
      value: formData.referents || [],
    },
  ];

  return (
    <Dialog open={isOpen}>
      <DialogTitle>{workshop.workshopName}</DialogTitle>
      <DialogContent>
        <FieldSection fields={dialogFields} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Abbrechen</Button>
        <Button onClick={handleSave} disabled={errorDate || errorStartTime || errorEndTime}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddWorkshopInstanceDialog;
